import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { DeferredPromise } from '@cf-shared/shared-util';
import { AnimationController } from 'providers/animation-controller';
import { MenuController } from 'providers/menu-controller';
import { DestroyGuard, getSideMenuWidth } from 'services/browser-utils';

export function getMainMenuWidth(): number {
  return getSideMenuWidth();
}

@Component({
    selector: 'cs-menu',
    templateUrl: 'cs-menu.html',
    host: { 'class': 'menu' },
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsMenuComponent implements OnDestroy {
  private destroyGuard = new DestroyGuard();
  @Output() onOpen: EventEmitter<void> = new EventEmitter<void>();
  @Output() onClose: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('innerMenu', { static: true }) innerMenu: ElementRef;
  @ViewChild('cover', { static: true }) coverElement: ElementRef;

  opened = false;

  openingPromise: DeferredPromise<void> | undefined = undefined;
  closingPromise: DeferredPromise<void> | undefined = undefined;

  constructor(
    private menuController: MenuController,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private animationController: AnimationController
  ) {
    this.menuController.registerHost(this);
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
  }

  ngOnInit() {
    this.enable(true);
    let element = this.elementRef.nativeElement as HTMLElement;
    element.classList.add('split-pane-side');

    let coverElement = this.coverElement.nativeElement as HTMLElement;
    coverElement.addEventListener('click', () => { void this.close(); });
  }

  public enable(enable: boolean) {
    let element = this.elementRef.nativeElement as HTMLElement;
    if (enable) {
      element.classList.add('menu-enabled');
    } else {
      element.classList.remove('menu-enabled');
    }
  }

  public async close() {
    if (!this.opened) {
      return;
    }
    this.closingPromise = DeferredPromise.defer<void>();

    let innerMenu = this.innerMenu.nativeElement as HTMLElement;
    let targetRect = innerMenu.getBoundingClientRect();

    let toX = `${-targetRect.width}px`;
    let menuAnimation = innerMenu.animate(
      [
        {
          transform: `translateX(0px)`,
        },
        {
          transform: `translateX(${toX})`,
        }
      ],
      {
        duration: 200, // duration in milliseconds
        fill: 'forwards', // ensures the animation stays at the end state
        easing: 'ease-out', // easing function
      }
    );

    const coverElement = this.coverElement.nativeElement;
    const coverAnimation = coverElement.animate(
      [
        { background: 'rgba(0, 0, 0, 0.5)', filter: 'blur(1px)' },
        { background: 'transparent', filter: 'blur(0px)' },
      ],
      {
        duration: 200, // duration in milliseconds
        fill: 'forwards', // ensures the animation stays at the end state
        easing: 'ease-out', // easing function
      }
    );

    // Wait for both animations to finish
    await this.animationController.safeAwaitAnimations([coverAnimation, menuAnimation]);

    if (this.destroyGuard.isDestroyed) return;
    this.opened = false;
    this.cdr.markForCheck();
    this.onClose.emit();
    this.closingPromise?.resolve();

    let element = this.elementRef.nativeElement as HTMLElement;
    element.style.removeProperty('display');
    element.classList.remove('show-menu');
  }

  public async open() {
    if (!this.openingPromise) {
      this.openingPromise = DeferredPromise.defer<void>();
    }

    this.opened = true;
    this.cdr.markForCheck();
    this.onOpen.emit();
    let element = this.elementRef.nativeElement as HTMLElement;
    element.style.setProperty('display', 'block');

    let innerMenu = this.innerMenu.nativeElement as HTMLElement;
    let targetRect = innerMenu.getBoundingClientRect();
    let fromX = `${-targetRect.width}px`;
    let menuAnimation = innerMenu.animate(
      [
        {
          transform: `translateX(${fromX})`,
        },
        {
          transform: `translateX(0px)`,
        }
      ],
      {
        duration: 200, // duration in milliseconds
        fill: 'forwards', // ensures the animation stays at the end state
        easing: 'ease-out', // easing function
      }
    );

    const coverElement = this.coverElement.nativeElement;
    const coverAnimation = coverElement.animate(
      [
        { background: 'transparent', filter: 'blur(0px)' },
        { background: 'rgba(0, 0, 0, 0.5)', filter: 'blur(1px)' },
      ],
      {
        duration: 200, // duration in milliseconds
        fill: 'forwards', // ensures the animation stays at the end state
        easing: 'ease-out', // easing function
      }
    );

    // Wait for both animations to finish
    await this.animationController.safeAwaitAnimations([coverAnimation, menuAnimation]);

    element.classList.add('show-menu');
    this.openingPromise?.resolve();
  }

  public isOpen(): boolean {
    return this.opened;
  }

  public async waitMenuTransitions() {
    let waitList = [];
    if (this.openingPromise) {
      waitList.push(this.openingPromise);
    }
    if (this.closingPromise) {
      waitList.push(this.closingPromise);
    }
    if (waitList.length > 0) {
      await Promise.all(waitList);
    }
  }
}

